import * as React from "react";
import { apiClientFactory } from "../../helpers";
import { KeyRequestCredentialType, LockResponse } from "../../api/ApiClient";
import { Spinner } from "../shared/Spinner";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import SearchInput from "components/shared/SearchInput";
import { Button } from "components/ui/button";

interface Injected {}

type OwnProps = {
  accessControlGroupId: string;
};

type Props = Injected & OwnProps;

interface EnrichedLockResponse extends LockResponse {
  assigned: boolean;
}

interface GroupedLocks {
  siteId: string;
  siteName: string;
  locks: EnrichedLockResponse[];
}

interface LockListBySiteProps {
  groupedLocks: GroupedLocks;
  searchTerm: string;
  accessControlGroupId: string;
  refresh: () => Promise<void>;
}

function LockListBySite(props: LockListBySiteProps) {
  const factory = apiClientFactory();
  const [expanded, setExpanded] = useState<boolean>(false);

  const [assignedLocks, setAssignedLocks] = useState<EnrichedLockResponse[]>(
    [],
  );
  const [availableLocks, setAvailableLocks] = useState<EnrichedLockResponse[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleAddLock = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    accessControlGroupId: string,
    lockExternalId: string,
    siteId: string,
  ): Promise<void> => {
    setIsLoading(true);
    e.preventDefault();
    await factory.addLockToAccessControlGroup(accessControlGroupId, {
      externalId: lockExternalId,
      siteId: siteId,
      credentialType: KeyRequestCredentialType.MobileKey,
    });
    await props.refresh();
    setIsLoading(false);
  };

  const handleDeleteLock = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    accessControlGroupId: string,
    lockId: string,
  ): Promise<void> => {
    setIsLoading(true);
    e.preventDefault();
    await factory.deleteLockFromAccessControlGroup(
      accessControlGroupId,
      lockId,
    );
    await props.refresh();
    setIsLoading(false);
  };

  useEffect(() => {
    const lockMatchesSearchTerm = (lock: EnrichedLockResponse): boolean => {
      return (
        (lock.name !== undefined &&
          lock.name.toLowerCase().includes(props.searchTerm.toLowerCase())) ||
        (lock.systemLockId !== undefined &&
          lock.systemLockId
            .toLowerCase()
            .includes(props.searchTerm.toLowerCase())) ||
        (lock.doorType !== undefined &&
          lock.doorType.toLowerCase().includes(props.searchTerm.toLowerCase()))
      );
    };

    setIsLoading(true);
    setExpanded(
      props.groupedLocks.locks.findIndex(
        (lock) =>
          lock.assigned === true ||
          (props.searchTerm !== "" && lockMatchesSearchTerm(lock)),
      ) !== -1,
    );
    setAssignedLocks(
      props.groupedLocks.locks.filter(
        (lock) =>
          lock.assigned &&
          (props.searchTerm === "" || lockMatchesSearchTerm(lock)),
      ),
    );
    setAvailableLocks(
      props.groupedLocks.locks.filter(
        (lock) =>
          !lock.assigned &&
          (props.searchTerm === "" || lockMatchesSearchTerm(lock)),
      ),
    );
    setIsLoading(false);
  }, [props.searchTerm, props.groupedLocks]);

  return (
    <div className="rounded-md border border-gray-200 bg-white shadow-sm">
      <div className="border-b px-4 py-3">
        <div className="flex w-full items-center justify-between">
          <span className="text-sm font-medium text-gray-700">
            {props.groupedLocks.siteName}
          </span>
          <Button
            aria-expanded={expanded}
            onClick={() => setExpanded(!expanded)}
            size="sm"
            type="button"
          >
            <i
              className={expanded ? "fa fa-minus" : "fa fa-plus"}
              aria-hidden="true"
            ></i>
          </Button>
        </div>
      </div>
      <div className={expanded ? "block" : "hidden"}>
        <div className="p-4">
          <h5 className="mb-2 text-sm font-semibold text-gray-800">
            Assigned Locks
          </h5>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-28 px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500" />
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Name
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Door Type
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    SystemLockId
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {assignedLocks.map((lock, i) => (
                  <tr key={"f_" + i} className="hover:bg-gray-50 h-20">
                    <td className="px-4 py-2 gap-4">
                      <Button
                        className="border-destructive text-destructive hover:bg-destructive/10 hover:text-destructive"
                        disabled={isLoading}
                        onClick={(e) =>
                          handleDeleteLock(
                            e,
                            props.accessControlGroupId,
                            lock.id as string,
                          )
                        }
                        size="sm"
                        type="button"
                        variant="outline"
                      >
                        Remove
                      </Button>
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {lock.name}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {lock.doorType}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {lock.systemLockId}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h5 className="mt-6 mb-2 text-sm font-semibold text-gray-800">
            Available Locks
          </h5>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-28 px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500" />
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Name
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Door Type
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    SystemLockId
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 mt-4">
                {availableLocks.map((lock, i) => (
                  <tr key={"f_" + i} className="hover:bg-gray-50 h-20">
                    <td className="px-4 py-2">
                      <Button
                        disabled={isLoading}
                        onClick={(e) =>
                          handleAddLock(
                            e,
                            props.accessControlGroupId,
                            lock.externalId as string,
                            lock.siteId as string,
                          )
                        }
                        size="sm"
                        type="button"
                        variant="outline"
                      >
                        Add
                      </Button>
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {lock.name}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {lock.doorType}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {lock.systemLockId}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function useGetLocks(accessControlGroupId: string) {
  const factory = apiClientFactory();

  const getLocks = async (): Promise<GroupedLocks[]> => {
    const [assignedLocks, locks] = await Promise.all([
      factory.getLocksForAccessControlGroup(
        accessControlGroupId,
        undefined,
        2147483647,
        undefined,
      ),
      factory.getLocks(undefined, 2147483647, undefined, undefined),
    ]);

    const enrichedLocks: EnrichedLockResponse[] = locks
      .map((e) => {
        const assigned =
          assignedLocks.findIndex(
            (assignedLock) =>
              assignedLock.lock !== undefined && assignedLock.lock.id === e.id,
          ) !== -1;
        return {
          ...e,
          assigned: assigned,
          selected: assigned,
          matched: false,
        };
      })
      .sort((a, b) =>
        (a.name ? a.name : "") > (b.name ? b.name : "") ? 1 : -1,
      );
    const groupedLocks = enrichedLocks.reduce((rv, x) => {
      let entry = rv.find((e) => e.siteId === x.siteId);
      if (!entry) {
        entry = {
          siteId: x.siteId ? x.siteId : "",
          siteName: x.site && x.site.name ? x.site.name : "",
          locks: [],
        };
        rv.push(entry);
      }
      entry.locks.push(x);
      return rv;
    }, [] as GroupedLocks[]);

    return groupedLocks;
  };

  return useQuery({
    queryKey: ["access-group-locks", accessControlGroupId],
    queryFn: getLocks,
  });
}

function SelectAccessControlGroupLock(props: Props) {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const { accessControlGroupId } = props;

  const {
    isLoading,
    data: groupedLocks,
    refetch,
  } = useGetLocks(accessControlGroupId);

  const refresh = async () => {
    await refetch();
  };

  const progressStyle: React.CSSProperties = {
    width: "100%",
  };

  return (
    <div>
      <h4>
        {searchTerm ? `Locks Matching '${searchTerm}'` : "Locks"}
        {isLoading && <Spinner />}
      </h4>
      {isLoading ? (
        <div className="rounded-md border border-blue-100 bg-blue-50 p-4">
          <div className="h-2 w-full overflow-hidden rounded bg-gray-200">
            <div className="h-2 w-full animate-pulse bg-blue-500" />
          </div>
          <div className="mt-2 text-sm text-blue-700">Loading</div>
        </div>
      ) : (
        <SearchInput
          name="typedAccount"
          placeholder="type here"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      )}

      <div className="space-y-4">
        {(groupedLocks || []).map((groupedLock: GroupedLocks) => (
          <LockListBySite
            key={groupedLock.siteId}
            groupedLocks={groupedLock}
            searchTerm={searchTerm}
            accessControlGroupId={accessControlGroupId}
            refresh={refresh}
          />
        ))}
      </div>
    </div>
  );
}

export default SelectAccessControlGroupLock;
